import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Donate.css';
import { load } from '@cashfreepayments/cashfree-js';
import PaymentMethodModal from '../components/PaymentMethodModal';

const Donate = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    amount: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.amount) {
      setError('Please fill all fields');
      return;
    }
    
    if (formData.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    // Show payment method selection modal
    setShowPaymentModal(true);
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setLoading(true);
    setError('');
    
    // Create payment order with selected method
    createPaymentOrder(method);
  };

  const createPaymentOrder = async (paymentMethod) => {
    try {
      const { name, email, phone, amount } = formData;
      
      // Simple routing: /{payment_method}/payment/process
      const apiEndpoint = `https://nqa1.in/api/${paymentMethod}/payment/process`;
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name,
          email: email,
          phone: phone,
          amount: parseFloat(amount)
        })
      });

      const data = await response.json();

      if (data.success) {
        // Close modal
        setShowPaymentModal(false);
        
        // Route to appropriate payment gateway based on method
        if (paymentMethod.toLowerCase() === 'cashfree' && data.data?.cashfree?.payment_session_id) {
          const sessionId = data.data.cashfree.payment_session_id;
          const orderId = data.data.order_id;
          initiateCashfreePayment(sessionId, orderId);
        } else if ((paymentMethod.toLowerCase() === 'phonepe' || paymentMethod.toLowerCase() === 'phone_pe') && data.data?.phonepe?.payment_url) {
          // Redirect to PhonePe payment URL
          const paymentUrl = data.data.phonepe.payment_url;
          window.location.href = paymentUrl;
        } else if (paymentMethod.toLowerCase() === 'razorpay' && data.data?.razorpay?.payment_session_id) {
          // Handle Razorpay integration here
          const sessionId = data.data.razorpay.payment_session_id;
          const orderId = data.data.order_id;
          initiateRazorpayPayment(sessionId, orderId);
        } else {
          setError(data.message || 'Payment gateway response invalid. Please try again.');
          setLoading(false);
        }
      } else {
        setError(data.message || 'Failed to create payment order. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating payment order:', error);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const initiateCashfreePayment = async (sessionId, orderId) => {
    try {
      const cashfree = await load({ mode: 'production' }); // or 'production'

      if (!cashfree) {
        setError('Payment gateway not loaded. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const checkoutOptions = {
        paymentSessionId: sessionId,
        redirectTarget: 'modal',
      };

      cashfree
        .checkout(checkoutOptions)
        .then(() => {
          // Payment completed, verify the payment
          verifyPayment(orderId, 'cashfree');
        })
        .catch((error) => {
          console.error('Cashfree checkout error:', error);
          setError('Payment initiation failed. Checking payment status...');
          // Even on failure/cancel, verify order status to route user appropriately
          verifyPayment(orderId, 'cashfree');
        });
    } catch (error) {
      console.error('Error initializing payment:', error);
      setError('Payment gateway failed to load. Please refresh and try again.');
      setLoading(false);
    }
  };

  const initiateRazorpayPayment = async (sessionId, orderId) => {
    try {
      // Razorpay integration can be added here when available
      setError('Razorpay integration is not yet available. Please use another payment method.');
      setLoading(false);
    } catch (error) {
      console.error('Error initializing Razorpay payment:', error);
      setError('Payment gateway failed to load. Please refresh and try again.');
      setLoading(false);
    }
  };

  const verifyPayment = async (orderId, paymentMethod) => {
    try {
      const response = await fetch(`https://nqa1.in/api/${paymentMethod}/payment/status?order_id=${orderId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      const status = data.data?.status || data.data?.data?.order_status || 'UNKNOWN';

      if (data.success && status === 'PAID') {
        navigate(`/thank-you?id=${orderId}&method=${paymentMethod}`);
      } else if (status === 'CANCELLED') {
        navigate(`/payment-cancelled?id=${orderId}&method=${paymentMethod}`);
      } else if (status === 'PAYMENT_FAILED' || status === 'FAILED') {
        const reason = encodeURIComponent(data.message || 'Payment failed');
        navigate(`/payment-failed?id=${orderId}&method=${paymentMethod}&reason=${reason}`);
      } else {
        navigate(`/payment-status?id=${orderId}&method=${paymentMethod}&status=${encodeURIComponent(status)}`);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error verifying payment:', error);
      const reason = encodeURIComponent('Verification error');
      navigate(`/payment-failed?id=${orderId}&reason=${reason}`);
    }
  };

  return (
    <div className="donate-page">
      <div className="container padding">
        <div className="heading">
          <h2>Donate To Our NGO</h2>
          <p>Help to needed!</p>
        </div>
        
        <div className="donate-content">
          <div className="donate-form-wrapper">
            <form onSubmit={handleSubmit} className="donate-form">
              {error && <div className="error-message" style={{ color: '#d32f2f', marginBottom: '15px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                {error}
              </div>}

              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter Your Full Name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-Mail Id</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter Your Email Id"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter Your Phone Number"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount">Donation Amount (₹)</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter Donation Amount"
                  min="1"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-btn">
                <button type="submit" className="btn read-more-btn" disabled={loading}>
                  {loading ? 'Processing...' : 'Donate Now'} <i className="fas fa-hand-holding-usd"></i>
                </button>
              </div>

              <div className="policy-notice">
                <p>
                  By making a donation, you agree to our{' '}
                  <Link to="/terms-and-conditions">Terms & Conditions</Link>,{' '}
                  <Link to="/refund-policy">Refund Policy</Link>, and{' '}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                  All donations are final and non-refundable.
                </p>
              </div>
            </form>
          </div>

          <PaymentMethodModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setLoading(false);
            }}
            onSelect={handlePaymentMethodSelect}
            loading={loading}
          />

          <div className="donate-info">
            <div className="info-card">
              <i className="fas fa-heart"></i>
              <h3>Your Impact</h3>
              <p>Every donation helps us reach more people in need and make a real difference in their lives.</p>
            </div>
            <div className="info-card">
              <i className="fas fa-shield-alt"></i>
              <h3>Secure Payment</h3>
              <p>All transactions are processed securely through Cashfree. Your payment information is safe.</p>
            </div>
            <div className="info-card">
              <i className="fas fa-certificate"></i>
              <h3>Transparency</h3>
              <p>We provide complete transparency about how your donations are used to help those in need.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;

